import {Component, OnDestroy, OnInit, PLATFORM_ID, inject} from '@angular/core';
import {DatePipe, isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {defaultIfEmpty} from 'rxjs/operators';
import {MatButton} from '@angular/material/button';
import {LoginService} from '../../core/components/login/login.service';
import {TranslationService} from '../../core/services/translation.service';
import {ConfigService} from '../../core/services/config.service';
import {BaseDataService} from '../../core/services/base-data.service';
import {NavigationCommand, NavigationService} from '../../data/services/navigation.service';
import {HupSubscription} from '../../data/models/subscription.model';
import {HupSubscriptionsService} from '../../data/services/hup-subscriptions.service';
import {ApplyIfNotEmptyDirective} from '../../core/directives/apply-if-not-empty.directive';
import {LoadingSpinnerComponent} from '../../core/components/loading-spinner/loading-spinner.component';
import {GeneralErrorsComponent} from '../../core/components/general-errors/general-errors.component';
import {SubscriptionCardComponent} from '../../core/components/subscription-card/subscription-card.component';
import {AdditionalBookingConfirmComponent} from './additional-booking-confirm/additional-booking-confirm.component';

/**
 * Anzeigezustand der Zubucherseite.
 */
export enum AdditionalBookingState {
  /** Anmeldestatus noch nicht bekannt - es wird ein neutraler Spinner gezeigt. */
  UNKNOWN = 'UNKNOWN',
  /** Nicht angemeldet - Anmeldung oder Identifikation werden angeboten. */
  GUEST = 'GUEST',
  /** Angemeldet - hier folgt die Prüfung auf Berechtigung (P5W-4460). */
  AUTHENTICATED = 'AUTHENTICATED'
}

/**
 * Warum steht kein Abonnement zum Zubuchen bereit?
 *
 * pca liefert für beide Fälle dieselbe leere Liste (SubscriptionService.validPrintToKombiPrintEpaper
 * filtert nicht vorgesehene UND bereits zugebuchte Abonnements heraus). Für den Kunden ist der
 * Unterschied aber wesentlich: im einen Fall gibt es nichts zu holen, im anderen hat er es schon.
 */
export enum NoBookingReason {
  /** Es gibt zubuchbare Abonnements - kein Hinweis nötig. */
  NONE = 'NONE',
  /** Kein Abonnement des Kunden ist für das Zubucher-Angebot vorgesehen. */
  NO_ELIGIBLE_SUBSCRIPTION = 'NO_ELIGIBLE_SUBSCRIPTION',
  /** Das zubuchbare Produkt läuft bereits - der Button führt auf genau dieses Abonnement. */
  ALREADY_BOOKED = 'ALREADY_BOOKED'
}

@Component({
  selector: 'app-additional-booking',
  templateUrl: './additional-booking.component.html',
  styleUrls: ['./additional-booking.component.scss'],
  imports: [
    ApplyIfNotEmptyDirective,
    LoadingSpinnerComponent,
    GeneralErrorsComponent,
    SubscriptionCardComponent,
    MatButton
  ],
  providers: [DatePipe],
  standalone: true
})
export class AdditionalBookingComponent implements OnInit, OnDestroy {

  /**
   * Das bereits laufende Produkt wird über seine Paradise-Auftragsnummer verlinkt; eine webId hat
   * es nicht. pca fällt bei webId 0 auf die Auftragsnummer zurück - dasselbe Muster nutzen die
   * Bestätigungsseite der Zubuchung und die Kündigung.
   */
  private static readonly WEB_ID_PARADISE_ONLY = '0';

  public translationService = inject(TranslationService);
  private loginService = inject(LoginService);
  private configService = inject(ConfigService);
  private baseDataService = inject(BaseDataService);
  private navigationService = inject(NavigationService);
  private subscriptionService = inject(HupSubscriptionsService);
  private datePipe = inject(DatePipe);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  public justRegistered: boolean = false;

  private state: AdditionalBookingState = AdditionalBookingState.UNKNOWN;
  private authSub: Subscription;
  /** Laufender Listen-Abruf - wird vor jedem neuen Abruf abbestellt, damit keine alte Antwort gewinnt. */
  private listSub: Subscription;
  /** Abmeldung des pageshow-Listeners; null beim Server-Rendering. */
  private removePageShowListener: () => void = null;
  protected subscriptions: HupSubscription[] = [];
  protected errors: string[] = [];
  protected isLoading = true;
  /** Nur bei leerer Liste gesetzt - steuert Hinweistext und Ziel des Buttons. */
  protected noBookingReason: NoBookingReason = NoBookingReason.NONE;
  /** Auftragsnummer des bereits laufenden Produkts, Ziel des Buttons "Zu diesem Abo". */
  protected bookedOrderNo: string = null;

  constructor() {
    this.justRegistered = !!this.router.getCurrentNavigation()?.extras?.state?.justRegistered;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering ist der Anmeldestatus nicht bekannt. Würde hier der Gast-Zustand
      // gerendert, sähe ein angemeldeter Nutzer kurz die Aufforderung zur Anmeldung.
      return;
    }
    // Der Anmeldestatus kann sich nachträglich ändern (läuft autoLogin/Token-Refresh noch, ist die
    // erste Emission null). Deshalb wird bei jeder Emission neu geladen und nicht einmalig synchron
    // nach dem subscribe – analog HupSubscriptionComponent.
    this.authSub = this.loginService.getAuthData().subscribe(authData => {
      this.state = authData ? AdditionalBookingState.AUTHENTICATED : AdditionalBookingState.GUEST;
      if (!this.isAuthenticated) {
        return;
      }
      this.loadExpandableSubscriptions();
    });
    this.listenForPageRestore();
  }

  /**
   * Holt die zubuchbaren Abonnements und setzt dabei den kompletten Anzeigezustand zurück.
   *
   * Eigene Methode, weil die Liste an mehr als einem Zeitpunkt frisch sein muss: beim Aufbau der
   * Seite (Anmeldestatus bekannt) und bei der Rückkehr aus dem Back/Forward-Cache.
   */
  private loadExpandableSubscriptions(): void {
    this.isLoading = true;
    this.errors = [];
    this.noBookingReason = NoBookingReason.NONE;
    this.bookedOrderNo = null;
    // Ein noch laufender Abruf würde sonst nach dem neuen antworten und die Liste wieder veralten.
    this.listSub?.unsubscribe();
    this.listSub = this.subscriptionService.getExpandableSubscriptions().subscribe({
      next: subs => {
        this.subscriptions = subs;
        if (subs.length === 0) {
          // Erst die Ursache klären, dann anzeigen - sonst blitzt der falsche Hinweis auf.
          this.determineNoBookingReason();
          return;
        }
        this.isLoading = false;
      },
      error: () => {
        this.subscriptions = [];
        this.errors = [this.translationService.getTranslation('error_subscription_init')];
        this.isLoading = false;
      }
    });
  }

  /**
   * Lädt die Liste neu, wenn der Browser die Seite aus seinem Back/Forward-Cache wiederherstellt.
   *
   * Bei einem solchen Restore wird die eingefrorene Seite samt JavaScript-Zustand zurückgeholt -
   * Angular baut die Komponente NICHT neu auf, ngOnInit läuft nicht, und es geht kein einziger
   * Request hinaus. Der Kunde sähe also genau die Liste, die vor seiner Zubuchung galt. Nur ein
   * Restore (`persisted`) löst das Nachladen aus; beim normalen Seitenaufbau feuert pageshow
   * ebenfalls, dort hat ngOnInit aber schon geladen.
   */
  private listenForPageRestore(): void {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted || !this.isAuthenticated) {
        return;
      }
      this.loadExpandableSubscriptions();
    };
    window.addEventListener('pageshow', onPageShow);
    this.removePageShowListener = () => window.removeEventListener('pageshow', onPageShow);
  }

  ngOnDestroy(): void {
    this.removePageShowListener?.();
    this.listSub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  /**
   * Klärt, warum nichts zugebucht werden kann: Das Produkt läuft bereits (dann ist auch seine
   * Auftragsnummer bekannt) oder kein Abonnement ist dafür vorgesehen.
   *
   * Jeder Fehlerpfad endet bewusst bei NO_ELIGIBLE_SUBSCRIPTION: Ohne belastbare Auskunft darf dem
   * Kunden kein Abonnement verlinkt werden, das er womöglich gar nicht hat.
   */
  private determineNoBookingReason(): void {
    const campaignId = this.configService
      .getConfig(AdditionalBookingConfirmComponent.CAMPAIGN_CONFIG_KEY)?.value;
    if (!campaignId) {
      console.error('Kein Werbeschlüssel konfiguriert: '
        + AdditionalBookingConfirmComponent.CAMPAIGN_CONFIG_KEY);
      this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
      return;
    }
    // getProductByCampaignId schluckt Fehler und liefert dann EMPTY - ohne defaultIfEmpty käme
    // weder next noch error, und die Seite bliebe dauerhaft im Ladezustand stehen.
    this.baseDataService.getProductByCampaignId(campaignId).pipe(defaultIfEmpty(null)).subscribe({
      next: product => {
        if (!product?.key) {
          console.error(`Zubuchbares Produkt für Werbeschlüssel ${campaignId} konnte nicht geladen werden`);
          this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
          return;
        }
        this.checkProductAlreadyBooked(product.key);
      },
      error: error => {
        console.error(`Zubuchbares Produkt für Werbeschlüssel ${campaignId} konnte nicht geladen werden`, error);
        this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
      }
    });
  }

  private checkProductAlreadyBooked(productCode: string): void {
    this.loginService.getUserData().subscribe({
      next: userData => this.checkProductForUser(userData.userBackendId, productCode),
      error: error => {
        console.error('Kundendaten für die Prüfung auf ein bestehendes Produkt konnten nicht geladen werden', error);
        this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
      }
    });
  }

  /**
   * Gefragt wird ab heute: ein Abonnement des Produkts, das heute oder später läuft, ist der Fall
   * "hat es bereits". Ein bereits beendetes Abonnement soll den Kunden nicht vom Zubuchen abhalten.
   */
  private checkProductForUser(userBackendId: number, productCode: string): void {
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.subscriptionService.checkIfCurrentProductAlreadyOrdered(userBackendId, productCode, today)
      .subscribe({
        next: response => {
          if (!response?.exists && response?.orderNo == '0') {
            this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
            return;
          }
          this.bookedOrderNo = response.orderNo;
          this.finishWithReason(NoBookingReason.ALREADY_BOOKED);
        },
        error: error => {
          console.error(`Prüfung auf das bereits gebuchte Produkt ${productCode} fehlgeschlagen`, error);
          this.finishWithReason(NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION);
        }
      });
  }

  private finishWithReason(reason: NoBookingReason): void {
    this.noBookingReason = reason;
    this.isLoading = false;
  }

  public get isStateUnknown(): boolean {
    return this.state === AdditionalBookingState.UNKNOWN;
  }

  public get isGuest(): boolean {
    return this.state === AdditionalBookingState.GUEST;
  }

  public get isAuthenticated(): boolean {
    return this.state === AdditionalBookingState.AUTHENTICATED;
  }

  /** Kein Abonnement des Kunden ist für das Zubucher-Angebot vorgesehen. */
  public get hasNoEligibleSubscription(): boolean {
    return this.noBookingReason === NoBookingReason.NO_ELIGIBLE_SUBSCRIPTION;
  }

  /** Das zubuchbare Produkt läuft bereits. */
  public get hasProductAlready(): boolean {
    return this.noBookingReason === NoBookingReason.ALREADY_BOOKED;
  }

  /** Führt auf die Bestätigungsseite der Zubuchung für das gewählte Abo. */
  public bookAdditionalProduct(subscription: HupSubscription): void {
    this.navigationService.navigateTo(
      NavigationCommand.ADDITIONALBOOKINGOVERVIEW, [String(subscription.subId)]);
  }

  /** Führt in die Abonnement-Übersicht des Kunden. */
  public goToSubscriptions(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION);
  }

  /** Führt auf das Abonnement, in dem das Produkt bereits läuft. */
  public goToBookedSubscription(): void {
    if (!this.bookedOrderNo) {
      // Ohne Auftragsnummer gibt es kein Ziel; die Übersicht ist die ehrlichere Auskunft.
      console.error('Kein Auftrag zum bereits gebuchten Produkt bekannt - Weiterleitung in die Abo-Übersicht');
      this.goToSubscriptions();
      return;
    }
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION,
      [AdditionalBookingComponent.WEB_ID_PARADISE_ONLY, this.bookedOrderNo]);
  }

  /** Führt zur Anmeldeseite, die nach erfolgreicher Anmeldung hierher zurückkehrt. */
  public goToLogin(): void {
    this.router.navigate([NavigationCommand.LOGIN], {
      queryParams: {returnUrl: NavigationCommand.ADDITIONALBOOKINGOVERVIEW}
    });
  }

  /** Führt in das Identifikationsformular, in dem die Registrierung erzwungen wird. */
  public goToIdentification(): void {
    this.navigationService.navigateTo(
      NavigationCommand.IDENTIFICATIONREGISTER,
      [NavigationCommand.ADDITIONALBOOKINGOVERVIEW.substring(1)]
    );
  }
}
